/** The UI language as the app sees it: which one is showing, how a
 *  person changes it, and how it stays in sync with their account
 *  (``users.ui_language``) across devices. */
import { useCallback, useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { keys, PREFERENCES_MUTATION_KEY, useMe, useUpdatePreferences } from "@/api/hooks";
import type { Me } from "@/api/types";
import { apiErrorText } from "@/features/sessions/errorMessages";
import { useOptionalToast } from "@/hooks/useToast";
import { detection } from "@/i18n";
import type { DetectionSource } from "@/i18n/detect";
import { entry, uiCodes, type LanguageEntry } from "@/i18n/languages";
import {
  clearPendingSync,
  hasPendingSync,
  readStoredUiLanguage,
  rememberExplicitChoice,
} from "@/i18n/persistence";

export interface SyncInput {
  /** The account's language; ``undefined`` when the backend doesn't
   *  report the field (older than migration 0093). */
  server: string | null | undefined;
  /** The language this page is showing (``i18n.language``). */
  current: string;
  /** The explicit choice stored on this device. */
  stored: string | null;
  /** A choice made signed out is waiting to be uploaded. */
  pending: boolean;
  /** How the start language was chosen on this page load. */
  source: DetectionSource;
  /** The codes selectable as UI language. */
  ui: ReadonlySet<string>;
  /** An account-preferences save is still in flight. */
  writing: boolean;
}

export type SyncAction =
  | { kind: "none" }
  | { kind: "clearPending" }
  | { kind: "upload"; code: string; clearPending: boolean }
  | { kind: "adopt"; code: string };

/** What to do when the account's language arrives or changes.
 *
 *  1. A choice made signed out (login or landing page) is uploaded,
 *     then the flag is cleared. The stored choice is uploaded rather
 *     than the showing language, so a ``?lng=`` override is never
 *     persisted.
 *  2. Otherwise the account wins: a language it holds that the UI can
 *     show is adopted and remembered on this device. Not while a save
 *     is in flight (the refetch would undo the pick being saved), and
 *     not over a ``?lng=`` override, which rules its page load.
 *  3. An account with no language gets this device's language, but
 *     only a real match (stored choice or browser language). The
 *     English default is never uploaded: a de-DE browser keeps a null
 *     account and gets German automatically once it ships — the
 *     server-side version of the old caching trap. */
export function planUiLanguageSync(input: SyncInput): SyncAction {
  const { server, current, stored, pending, source, ui, writing } = input;
  if (server === undefined) return { kind: "none" };

  if (pending) {
    const choice = stored !== null && ui.has(stored) ? stored : null;
    if (choice === null || choice === server) return { kind: "clearPending" };
    return { kind: "upload", code: choice, clearPending: true };
  }

  if (server !== null) {
    if (!ui.has(server) || server === current) return { kind: "none" };
    if (writing || source === "query") return { kind: "none" };
    return { kind: "adopt", code: server };
  }

  if ((source === "stored" || source === "navigator") && ui.has(current)) {
    return { kind: "upload", code: current, clearPending: false };
  }
  return { kind: "none" };
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

/** The last account value each query client's sync acted on, so the
 *  plan runs once per change: not again for StrictMode's second effect
 *  pass, nor when the next page mounts the hook with the same data. */
const handled = new WeakMap<QueryClient, string>();

/** Keep the UI language and the account's in sync. Mount once per
 *  signed-in tree (``AppShell``, and the standalone ``WelcomePage`` and
 *  ``InvitationAcceptPage``); it does nothing until ``/auth/me`` has
 *  answered with a user. Background saves fail quietly: rule 1 keeps
 *  its flag and retries on the next load, rule 3 simply runs again. */
export function useUiLanguageSync(): void {
  const me = useMe();
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const { mutateAsync } = useUpdatePreferences();
  const userId = me.data?.user.id;
  const server = me.data?.user.ui_language;

  useEffect(() => {
    if (!userId) return;
    const key = `${userId}|${server === undefined ? "?" : String(server)}`;
    if (handled.get(qc) === key) return;
    handled.set(qc, key);

    const action = planUiLanguageSync({
      server,
      current: i18n.language,
      stored: readStoredUiLanguage(),
      pending: hasPendingSync(),
      source: detection.source,
      ui: new Set(uiCodes()),
      writing: qc.isMutating({ mutationKey: PREFERENCES_MUTATION_KEY }) > 0,
    });
    switch (action.kind) {
      case "none":
        return;
      case "clearPending":
        clearPendingSync();
        return;
      case "adopt":
        rememberExplicitChoice(action.code, { authenticated: true });
        void i18n.changeLanguage(action.code);
        return;
      case "upload":
        mutateAsync({ ui_language: action.code }).then(
          () => {
            if (action.clearPending) clearPendingSync();
          },
          (err: unknown) => {
            // A refused code will never be accepted: stop retrying it.
            if (action.clearPending && httpStatus(err) === 422) clearPendingSync();
            console.warn("i18n: could not save the UI language to the account", err);
          },
        );
        return;
    }
  }, [userId, server, qc, i18n, mutateAsync]);
}

export interface UiLanguage {
  /** The language showing. */
  code: string;
  /** Its registry entry (undefined for the dev pseudo-locale). */
  entry: LanguageEntry | undefined;
  /** Switch to ``code``: shown at once, remembered on this device, and
   *  saved to the account when signed in (flagged for upload after
   *  login otherwise). The save never blocks the switch; a failure is
   *  reported in a toast (``ToastProvider``, which main.tsx mounts above
   *  every route; outside one it is only logged). */
  setUiLanguage: (code: string) => void;
}

export function useUiLanguage(): UiLanguage {
  const { i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const { mutateAsync } = useUpdatePreferences();
  const code = i18n.resolvedLanguage ?? i18n.language;

  const setUiLanguage = useCallback(
    (next: string) => {
      const authenticated = qc.getQueryData<Me>(keys.me) !== undefined;
      rememberExplicitChoice(next, { authenticated });
      void i18n.changeLanguage(next);
      if (!authenticated) return;
      mutateAsync({ ui_language: next }).catch((err: unknown) => {
        // Say it in the language just picked: that is what is showing.
        const text = apiErrorText(i18n.getFixedT(next), err);
        if (toast) toast(text, "error");
        else console.warn("i18n: could not save the UI language:", text);
      });
    },
    [i18n, qc, toast, mutateAsync],
  );

  return { code, entry: entry(code), setUiLanguage };
}
